import {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'

import Button from './mui_base_components/button/Button'
import LargeModal from './mui_base_components/modals/large_modal/LargeModal'

import downloadIcon from '../shared_assets/download-icon.svg'
import printIcon from '../shared_assets/print-icon.svg'

import './imageViewer.scss'

const ImageViewer = ({
  error = null,
  imageName = 'Image',
  imageSrc = null,
  isLoading,
  isModalOpen,
  onCloseModal,
  onDownloadClick
}) => {
  const [imageRenderError, setImageRenderError] = useState(false)

  const errorMessageElement = useCallback(() => (
    <div className='image-viewer__error-message-element'>
      <div className='image-viewer__error-message'>
        Preview not available. Please download to view.
      </div>
      <Button
        className='image-viewer__download-button'
        onClick={onDownloadClick}>
        Download
      </Button>
    </div>
  ), [onDownloadClick])

  useEffect(() => {
    if (!isModalOpen) return

    setImageRenderError(false)
  }, [imageSrc, isModalOpen])

  const handleClose = () => onCloseModal()

  const handlePrint = () => {
    if (!imageSrc) return

    const printWindow = window.open('')

    if (!printWindow) return

    const {document: printDocument} = printWindow

    printDocument.title = imageName

    const getOrCreateBody = () => {
      if (printDocument.body) return printDocument.body

      const htmlElement = printDocument.documentElement || printDocument.createElement('html')
      const headElement = printDocument.head || printDocument.createElement('head')
      const bodyElement = printDocument.createElement('body')

      if (!printDocument.documentElement)
        printDocument.appendChild(htmlElement)

      if (!printDocument.head)
        htmlElement.appendChild(headElement)

      htmlElement.appendChild(bodyElement)

      return bodyElement
    }

    const bodyElement = getOrCreateBody()

    bodyElement.innerHTML = ''
    bodyElement.style.margin = '0'

    const styleElement = printDocument.createElement('style')

    styleElement.textContent = `
      body { margin: 0; }
      img {
        display: block;
        max-width: 100%;
        height: auto;
        margin: 0 auto;
      }
    `

    if (printDocument.head)
      printDocument.head.appendChild(styleElement)
    else
      bodyElement.appendChild(styleElement)

    const imageElement = printDocument.createElement('img')

    imageElement.alt = imageName

    imageElement.onload = () => {
      printWindow.focus()
      printWindow.print()
    }

    imageElement.onerror = () => {
      if (!printWindow.closed)
        printWindow.close()

      // eslint-disable-next-line no-alert
      window.alert('Unable to load image for printing.')
    }

    imageElement.src = imageSrc

    bodyElement.appendChild(imageElement)
  }

  return (
    <LargeModal
      className='image-viewer'
      disablePortal={false}
      onClose={handleClose}
      open={isModalOpen}>
      <div className='content'>
        <div className='title'>{imageName}</div>
        <div
          aria-label='Image viewer'
          className='image-container'
          role='region'
          tabIndex={0}>
          {
            isLoading &&
            <div className='image-viewer__message'>Loading image...</div>
          }
          {
            !isLoading && error &&
            <div className='image-viewer__message error'>{error}</div>
          }
          {!isLoading && !error && !imageSrc && (
            <div className='image-viewer__message error'>
              Failed to load image
            </div>
          )}
          {!isLoading && imageRenderError && errorMessageElement()}
          {!isLoading && !imageRenderError && imageSrc && (
            <img
              alt={imageName}
              className='image-preview'
              onError={() => setImageRenderError(true)}
              src={imageSrc}
            />
          )}
        </div>
        {!isLoading && !error && !imageRenderError && imageSrc && (
          <div className='image-actions' data-testid='image-actions'>
            <button
              aria-label='Download image'
              className='icon-button'
              onClick={onDownloadClick}
              type='button'>
              <img
                alt=''
                aria-hidden='true'
                className='icon'
                src={downloadIcon}
              />
            </button>
            <button
              aria-label='Print image'
              className='icon-button'
              onClick={handlePrint}
              type='button'>
              <img
                alt=''
                aria-hidden='true'
                className='icon'
                src={printIcon}
              />
            </button>
          </div>
        )}
      </div>
    </LargeModal>
  )
}

ImageViewer.propTypes = {
  error: PropTypes.string,
  imageName: PropTypes.string,
  imageSrc: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onDownloadClick: PropTypes.func.isRequired
}

export default ImageViewer
